import {
  ShopDto,
  ShopExchangeShippingMethodsDto,
} from "@babackapp/private-api-client/types";
import { useCallback, useEffect, useState } from "react";
import { useCarriersContext } from "../use-carriers-context";
import useUpdateShopExchange from "@babackapp/private-api-client/client/use-update-shop-exchange";
import useTranslation from "@babackapp/translations/use-translation";
import { ChevronDown } from "lucide-react";

export const ShippingMethods = () => {
  const { shop } = useCarriersContext();
  const { mutate } = useUpdateShopExchange(shop);

  const onSave = useCallback(
    (shippingMethods: ShopExchangeShippingMethodsDto[]) => {
      mutate({ shippingMethods });
    },
    [],
  );
  return <ShippingMethodsEdition shop={shop} onSave={onSave} />;
};

function ShippingMethodsEdition({
  shop,
  onSave,
}: {
  shop: ShopDto;
  onSave: (shippingMethods: ShopExchangeShippingMethodsDto[]) => void;
}) {
  const t = useTranslation();
  const [shippingMethods, setShippingMethods] = useState<
    ShopExchangeShippingMethodsDto[]
  >(shop.exchange.shippingMethods);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  useEffect(() => {
    setShippingMethods(shop.exchange.shippingMethods);
  }, [shop]);

  const onSelectIsPickingPoint = (
    isPickingPoint: boolean,
    method: ShopExchangeShippingMethodsDto,
  ) => {
    const index = shippingMethods.findIndex((m) => m.code === method.code);
    shippingMethods[index].isPickingPoint = isPickingPoint;
    onSave([...shippingMethods]);
    setShippingMethods([...shippingMethods]);
  };

  const onSelectExchangeOverride = (
    overrideCode: string | null,
    method: ShopExchangeShippingMethodsDto,
  ) => {
    const index = shippingMethods.findIndex((m) => m.code === method.code);
    if (overrideCode) {
      const target = shippingMethods.find((m) => m.code === overrideCode);
      if (target) {
        shippingMethods[index].exchangeOverride = {
          code: target.code ?? "",
          title: target.title,
        };
      }
    } else {
      shippingMethods[index].exchangeOverride = undefined;
    }
    onSave([...shippingMethods]);
    setShippingMethods([...shippingMethods]);
  };

  const formatPrice = (amount?: string, currency?: string) => {
    if (!amount || !currency) return null;
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency,
    }).format(numAmount);
  };

  const isExpanded = (method: ShopExchangeShippingMethodsDto) =>
    expandedCode === method.code;

  const toggleExpand = (method: ShopExchangeShippingMethodsDto) => {
    setExpandedCode(isExpanded(method) ? null : (method.code ?? null));
  };

  return (
    <div className="pl-8 text-sm sm:pt-6 space-y-4">
      <div className="space-y-2">
        {shippingMethods.map((method) => (
          <div key={method.code} className="space-y-1">
            <div className="space-x-4 flex items-center">
              <div className="flex-1 flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  className="shrink-0 text-gray-400 hover:text-gray-600"
                  onClick={() => toggleExpand(method)}
                >
                  <ChevronDown
                    className={`size-3 transition-transform ${isExpanded(method) ? "" : "-rotate-90"}`}
                  />
                </button>
                <div className="space-x-2 min-w-0">
                  <span className="truncate inline-block max-w-80 align-bottom" title={method.title}>{method.title}</span>
                  {method.originalAmount && (
                    <span className="text-gray-500 text-xs">
                      {formatPrice(
                        method.originalAmount,
                        method.currencyCode ?? undefined,
                      )}
                    </span>
                  )}
                  {method.exchangeOverride && (
                    <span className="text-purple-600 text-xs">
                      → {method.exchangeOverride.title}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex text-gray-600">
                <span className="mt-0.5">
                  <RadioComponent
                    checked={method.isPickingPoint}
                    onChange={() => onSelectIsPickingPoint(true, method)}
                  />
                </span>
                <span className="ml-2 mr-4">
                  {t("settings.config.pickingPoint")}
                </span>
                <span className="mt-0.5">
                  <RadioComponent
                    checked={!method.isPickingPoint}
                    onChange={() => onSelectIsPickingPoint(false, method)}
                  />
                </span>
                <span className="ml-2">
                  {t("settings.config.homeDelivery")}
                </span>
              </div>
            </div>
            {isExpanded(method) && (
              <div className="pl-6 flex items-center gap-2 text-xs text-gray-500">
                <span>{t("settings.config.exchangeOverride")}:</span>
                <select
                  className="max-w-48 truncate rounded border border-gray-200 px-2 py-1 text-xs"
                  value={method.exchangeOverride?.code ?? ""}
                  onChange={(e) =>
                    onSelectExchangeOverride(
                      e.target.value || null,
                      method,
                    )
                  }
                >
                  <option value="">
                    {t("settings.config.noOverride")}
                  </option>
                  {shippingMethods
                    .filter((m) => m.code !== method.code)
                    .map((m) => (
                      <option key={m.code} value={m.code ?? ""}>
                        {m.title}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export const RadioComponent = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
}) => {
  if (checked) {
    return (
      <div
        className="rounded-full h-4 w-4 border-4 border-purple-600"
        onClick={() => onChange(!checked)}
      ></div>
    );
  }
  return (
    <div
      className="rounded-full cursor-pointer h-4 w-4 border border-gray-200 hover:border-4"
      onClick={() => onChange(!checked)}
    ></div>
  );
};
